import { useChatStore } from "@/store/chat";
import { useEffect, useState } from "react";
import { Mic, Square, ArrowUp } from "lucide-react";
import { Avatar } from "@/features/chat/components/avatar";
import { HandlerScrollThread } from "./handler-scroll-thread";
import type { ExtraCredit } from "./voice-report";
import "./handler-debrief-chat.css";
export type ReportMessage = { text: string; member: boolean };
export function HandlerDebriefChat({
  name,
  messages,
  waiting,
  recording,
  busy,
  error,
  answer,
  setAnswer,
  hasTranscript,
  record,
  stop,
  send,
  complete,
  finish,
  retry,
  connected,
  levels,
  extraCredit,
  onPause,
}: {
  name: string;
  messages: ReportMessage[];
  waiting: boolean;
  recording: boolean;
  busy: boolean;
  error: string;
  answer: string;
  setAnswer: (text: string) => void;
  hasTranscript: boolean;
  record: (append: boolean) => void;
  stop: () => void;
  send: () => void;
  complete: boolean;
  finish: () => void;
  retry: () => void;
  connected: boolean;
  levels: number[];
  extraCredit?: ExtraCredit;
  onPause?: () => void;
}) {
  const [textMode, setTextMode] = useState(false);
  useEffect(() => {
    useChatStore.getState().enterEmbeddedChat();
    return () => useChatStore.getState().leaveEmbeddedChat();
  }, []);
  return (
    <section
      className="handler-report-chat"
      aria-label={`Debrief with ${name}`}
    >
      <header>
        <Avatar name={name} ai />
        <div>
          <small>Your handler · Report in</small>
          <h2>{name}</h2>
        </div>
      </header>
      <HandlerScrollThread
        className="handler-report-thread"
        role="log"
        aria-label="Debrief conversation"
      >
        {extraCredit && extraCredit.status !== "completed" && (
          <div className="handler-report-extra-credit" role="status">
            <strong>Extra Credit</strong>
            <p>{extraCredit.task}</p>
          </div>
        )}
        {messages.map((message, index) => (
          <div
            key={index}
            className="handler-report-row"
            data-member={message.member}
          >
            <div className="handler-report-bubble">
              {!message.member && <strong>{name}</strong>}
              <p>{message.text}</p>
            </div>
          </div>
        ))}
        {waiting && (
          <div
            className="handler-report-row"
            role="status"
            aria-label={`${name} is typing`}
          >
            <div className="handler-report-bubble handler-report-typing">
              <i />
              <i />
              <i />
            </div>
          </div>
        )}
      </HandlerScrollThread>
      <div className="handler-report-compose">
        {error && <p role="alert">{error}</p>}
        {!connected && !waiting && (
          <button type="button" onClick={retry}>
            Retry connection
          </button>
        )}
        {complete ? (
          <button
            type="button"
            className="handler-report-primary"
            onClick={finish}
          >
            Review my report
          </button>
        ) : (
          <>
            {(textMode || hasTranscript) && (
              <div className="handler-report-input">
                <textarea
                  aria-label={textMode ? "Your reply" : "Check your answer"}
                  placeholder={`Reply to ${name}\u2026`}
                  value={answer}
                  rows={2}
                  maxLength={6000}
                  disabled={busy || waiting}
                  onChange={(e) => setAnswer(e.target.value)}
                />
                {answer.trim() && (
                  <button
                    type="button"
                    aria-label="Send answer"
                    disabled={busy || waiting || !connected}
                    onClick={send}
                  >
                    <ArrowUp size={22} />
                  </button>
                )}
              </div>
            )}
            {!textMode && !hasTranscript && (
              <button
                type="button"
                className="handler-report-mic"
                disabled={waiting || !connected || (busy && !recording)}
                onClick={() => (recording ? stop() : record(false))}
              >
                {recording ? <Square size={22} /> : <Mic size={26} />}
                <span>
                  {recording
                    ? "Stop recording"
                    : busy
                      ? "Processing\u2026"
                      : "Tap to speak"}
                </span>
                {recording && (
                  <span className="handler-report-wave" aria-hidden="true">
                    {levels.map((level, i) => (
                      <i key={i} style={{ height: 3 + level * 24 }} />
                    ))}
                  </span>
                )}
              </button>
            )}
            {!textMode && hasTranscript && (
              <div className="handler-report-record-actions">
                <button
                  type="button"
                  disabled={busy || waiting}
                  onClick={() => record(false)}
                >
                  Re-record
                </button>
                <button
                  type="button"
                  disabled={busy || waiting}
                  onClick={() => record(true)}
                >
                  Add more
                </button>
                {recording && (
                  <button type="button" onClick={stop}>
                    Stop recording
                  </button>
                )}
              </div>
            )}
            <button
              type="button"
              className="handler-report-toggle"
              disabled={busy || waiting}
              onClick={() => setTextMode(!textMode)}
            >
              {textMode ? "Use voice" : "Can't talk"}
            </button>
            {onPause && (
              <button
                type="button"
                className="handler-report-toggle"
                onClick={onPause}
              >
                I'll come back
              </button>
            )}
          </>
        )}
      </div>
    </section>
  );
}
